import { useCallback, useState } from "react";
import { Activity, Gauge, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { RemotePageState } from "../components/RemotePageState";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Drawer as V3Drawer } from "../components/Overlay";
import { StatusBadge } from "../components/StatusBadge";

type BusinessHealthStatus = "available" | "performance_degraded" | "degraded" | "unavailable" | "unconfigured" | string;
type BusinessHealthItem = {
  key: string;
  label: string;
  status: BusinessHealthStatus;
  summary: string;
  impact: string;
  recovery: string;
  updatedAtText: string;
  evidenceAvailable: boolean;
  technicalEvidenceToken: string;
};
type BusinessHealthResponse = { items?: unknown[] };
type EvidenceState = { status: "idle" | "loading" | "ready" | "error"; title: string; items: string[]; message: string };

const healthStatusLabels: Record<string, string> = {
  available: "可用",
  performance_degraded: "性能下降",
  degraded: "性能下降",
  unavailable: "不可用",
  unconfigured: "未配置",
};
const healthStatusBadgeValue: Record<string, string> = {
  available: "healthy",
  performance_degraded: "processing",
  degraded: "processing",
  unavailable: "failed",
  unconfigured: "unchecked",
};
const safeHealthLabels: Record<string, string> = {
  database: "数据服务",
  notifications: "通知能力",
  billing: "账务链路",
  identity: "登录能力",
  delivery: "投递能力",
};

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isTechnical(value: string): boolean {
  return /^[a-z0-9][a-z0-9_.:-]*$/i.test(value.trim());
}

function businessText(value: unknown, fallback: string): string {
  const text = stringOf(value);
  if (!text || isTechnical(text)) return fallback;
  return text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "业务对象").slice(0, 260);
}

function statusText(value: BusinessHealthStatus): string {
  return healthStatusLabels[value] ?? "状态待确认";
}

function formatHealthTime(value: unknown): string {
  const text = stringOf(value);
  if (!text) return "尚无检查记录";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "尚无检查记录";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function normalizeHealth(response: BusinessHealthResponse): BusinessHealthItem[] {
  return arrayOf(response.items).map((item) => {
    const record = recordOf(item);
    const key = stringOf(record.key) ?? "";
    const status = stringOf(record.status) ?? "unconfigured";
    const technicalEvidenceToken = stringOf(record.technicalEvidenceToken ?? record.evidenceToken) ?? "";
    return {
      key,
      label: safeHealthLabels[key] ?? businessText(record.businessLabel ?? record.label, "业务能力"),
      status,
      summary: businessText(record.summary, status === "unconfigured" ? "尚未配置业务级健康证据" : "业务状态等待平台确认"),
      impact: businessText(record.impact, status === "unconfigured" ? "未配置时不作为可用能力承诺" : "影响范围等待平台确认"),
      recovery: businessText(record.recovery, status === "unconfigured" ? "完成配置并产生业务证据后再判断" : "按业务恢复指引处理"),
      updatedAtText: formatHealthTime(record.updatedAt ?? record.checkedAt ?? record.lastCheckedAt),
      evidenceAvailable: Boolean(technicalEvidenceToken),
      technicalEvidenceToken,
    };
  });
}

function badgeValue(status: BusinessHealthStatus): string {
  return healthStatusBadgeValue[status] ?? "pending";
}

export function HealthPage() {
  const loader = useCallback(async (): Promise<BusinessHealthItem[]> => normalizeHealth(await api<BusinessHealthResponse>("/api/health/business")), []);
  const remote = useRemote(loader, (items) => items.length === 0);
  const [drawerItem, setDrawerItem] = useState<BusinessHealthItem | null>(null);
  const [evidenceState, setEvidenceState] = useState<EvidenceState>({ status: "idle", title: "", items: [], message: "" });
  const columns: Array<DataTableColumn<BusinessHealthItem>> = [
    { key: "capability", header: "能力", width: "24%", render: (item) => <span className="health-business-main"><strong>{item.label}</strong><small>{item.summary}</small></span> },
    { key: "status", header: "业务状态", width: "16%", render: (item) => <span className="health-business-main"><StatusBadge value={badgeValue(item.status)} /><small>{statusText(item.status)}</small></span> },
    { key: "updated", header: "最后检查", width: "16%", render: (item) => <span className="app-number">{item.updatedAtText}</span> },
    { key: "impact", header: "影响", width: "22%", render: (item) => item.impact },
    { key: "recovery", header: "恢复入口", width: "22%", render: (item) => item.recovery },
  ];
  async function openEvidence(item: BusinessHealthItem) {
    setDrawerItem(item);
    if (!item.technicalEvidenceToken) {
      setEvidenceState({ status: "error", title: `${item.label}证据`, items: [], message: "平台尚未返回技术证据凭证，暂不能查看。请刷新或完成业务级验证后再试。" });
      return;
    }
    setEvidenceState({ status: "loading", title: `${item.label}证据`, items: [], message: "正在读取技术证据…" });
    try {
      const data = await api<{ title?: string; items?: unknown[] }>(`/api/health/business/evidence/${encodeURIComponent(item.technicalEvidenceToken)}`);
      setEvidenceState({
        status: "ready",
        title: businessText(data.title, `${item.label}证据`),
        items: arrayOf(data.items).map((entry) => businessText(entry, "证据由平台保留")).slice(0, 12),
        message: "",
      });
    } catch (failure) {
      setEvidenceState({ status: "error", title: `${item.label}证据`, items: [], message: failure instanceof Error ? failure.message : "技术证据读取失败，请稍后重试。" });
    }
  }

  function closeEvidence() {
    setDrawerItem(null);
    setEvidenceState({ status: "idle", title: "", items: [], message: "" });
  }

  return <div className="page health-business-page">
    <PageHeader
      icon={Activity}
      title="业务健康"
      meta={<span>只展示业务可用性；未配置、性能下降和不可用都不会显示为绿色。</span>}
    />
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      emptyHint="当前没有业务健康项。请先完成服务配置和业务级验证，再查看可用性。"
    >{(items) => <SectionCard
      icon={Gauge}
      title="业务健康"
      meta={<span><strong className="app-number">{items.length}</strong> 个能力</span>}
      variant="ledger"
    >
      <DataTable
        ariaLabel="业务健康"
        columns={columns}
        rows={items}
        rowKey={(item) => item.key || item.label}
        empty={<span>暂无业务健康证据。</span>}
        rowActions={(item) => item.evidenceAvailable
          ? <button type="button" className="button secondary" onClick={() => void openEvidence(item)}><ShieldCheck size={15} />查看证据</button>
          : <span className="health-evidence-muted">暂无证据</span>}
      />
    </SectionCard>}</RemotePageState>
    {drawerItem && <V3Drawer
      title={evidenceState.title || `${drawerItem.label}证据`}
      description="技术探针和底层检查只在这里作为证据展示，不替代业务可用结论。"
      onClose={closeEvidence}
      footer={<button type="button" className="button secondary" onClick={closeEvidence}>关闭</button>}
    >
      <div className="health-evidence-drawer">
        <span className="health-business-main"><StatusBadge value={badgeValue(drawerItem.status)} /><small>最后检查：<span className="app-number">{drawerItem.updatedAtText}</span></small></span>
        <p>{drawerItem.summary}</p>
        {evidenceState.status === "loading" && <p role="status">正在读取技术证据…</p>}
        {evidenceState.status === "error" && <div className="form-error" role="alert">{evidenceState.message}</div>}
        {evidenceState.status === "ready" && <ul>
          {(evidenceState.items.length ? evidenceState.items : ["暂无可展示证据，请先完成业务级验证。"]).map((item) => <li key={item}>{item}</li>)}
        </ul>}
      </div>
    </V3Drawer>}
  </div>;
}
